import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  LayoutDashboard, Users, Globe, MessageSquare, Settings, LogOut, 
  Plus, Pencil, Trash2, Eye, EyeOff, Server, Activity, AlertTriangle,
  Lock, Search, Bell, ChevronDown, X, Check, Loader2, BarChart3, Clock, Play, Menu,
  Store, Shield, Gauge, Stethoscope
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ResellersPage from "./pages/ResellersPage";
import ResellerLayout from "./pages/ResellerLayout";
import BandwidthPage from "./pages/BandwidthPage";
import DiagnosticsPage from "./pages/DiagnosticsPage";

// Backend URL: in produzione usa SEMPRE window.location.origin (così il bundle è
// portable su qualsiasi VPS senza rebuild). In dev usa REACT_APP_BACKEND_URL.
const BACKEND_URL = (typeof window !== 'undefined' && process.env.NODE_ENV === 'production')
  ? window.location.origin
  : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${BACKEND_URL}/api`;

// Global axios interceptor: su 401 invalida sessione e torna al login.
// Questo evita "pagine vuote" quando il JWT access scade (30min in v2).
axios.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    // Evita loop di redirect sul login stesso
    if (status === 401 && !url.endsWith("/admin/login") && !url.endsWith("/auth/refresh")) {
      try {
        localStorage.removeItem("sktv_admin_token");
        localStorage.removeItem("sktv_admin_refresh");
      } catch (e) { console.warn("storage cleanup fallita", e); }
      delete axios.defaults.headers.common["Authorization"];
      if (!window.location.pathname.endsWith("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem("sktv_admin_token"));
  const [admin, setAdmin] = useState(() => {
    try {
      const raw = localStorage.getItem("sktv_admin_user");
      return raw ? JSON.parse(raw) : null;
    } catch (e) { console.warn("admin user non leggibile da storage", e); return null; }
  });
  const [role, setRole] = useState(localStorage.getItem("sktv_role") || "admin");

  useEffect(() => {
    if (token) {
      localStorage.setItem("sktv_admin_token", token);
      localStorage.setItem("sktv_role", role);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      localStorage.removeItem("sktv_admin_token");
      localStorage.removeItem("sktv_admin_refresh");
      localStorage.removeItem("sktv_admin_user");
      localStorage.removeItem("sktv_role");
      delete axios.defaults.headers.common["Authorization"];
    }
  }, [token, role]);

  // Persisti admin object così sopravvive al refresh
  useEffect(() => {
    if (admin) {
      try { localStorage.setItem("sktv_admin_user", JSON.stringify(admin)); } catch (e) { console.warn("persist admin fallita", e); }
    }
  }, [admin]);

  // Setup access token IMMEDIATAMENTE allo start (prima del primo render figli)
  // così la prima chiamata API non parte senza header.
  if (token && !axios.defaults.headers.common["Authorization"]) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }

  // Axios interceptor: auto-refresh access_token quando scaduto (401).
  // Evita che dopo un refresh pagina o dopo 30 min l'admin venga sloggato.
  useEffect(() => {
    let isRefreshing = false;
    let pendingQueue = [];
    const flushQueue = (err, newToken) => {
      pendingQueue.forEach((p) => err ? p.reject(err) : p.resolve(newToken));
      pendingQueue = [];
    };
    const interceptorId = axios.interceptors.response.use(
      (r) => r,
      async (error) => {
        const originalReq = error.config;
        const status = error.response?.status;
        const refresh = localStorage.getItem("sktv_admin_refresh");
        // Tenta refresh solo su 401, una volta sola, e solo se abbiamo un refresh_token.
        if (status === 401 && refresh && !originalReq._retried &&
            !originalReq.url?.includes("/auth/refresh") &&
            !originalReq.url?.includes("/admin/login")) {
          originalReq._retried = true;
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              pendingQueue.push({ resolve: (t) => {
                originalReq.headers["Authorization"] = `Bearer ${t}`;
                resolve(axios(originalReq));
              }, reject });
            });
          }
          isRefreshing = true;
          try {
            const r = await axios.post(`${API}/auth/refresh`, {}, {
              headers: { Authorization: `Bearer ${refresh}` }
            });
            const newAccess = r.data.access_token || r.data.token;
            const newRefresh = r.data.refresh_token;
            localStorage.setItem("sktv_admin_token", newAccess);
            if (newRefresh) localStorage.setItem("sktv_admin_refresh", newRefresh);
            axios.defaults.headers.common["Authorization"] = `Bearer ${newAccess}`;
            setToken(newAccess);
            flushQueue(null, newAccess);
            originalReq.headers["Authorization"] = `Bearer ${newAccess}`;
            return axios(originalReq);
          } catch (e) {
            flushQueue(e);
            // Refresh fallito → logout pulito.
            localStorage.removeItem("sktv_admin_token");
            localStorage.removeItem("sktv_admin_refresh");
            delete axios.defaults.headers.common["Authorization"];
            setToken(null);
            return Promise.reject(e);
          } finally {
            isRefreshing = false;
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
    // Registrato UNA volta al mount by-design: setToken è stabile e il refresh
    // token viene letto fresco da localStorage a ogni 401.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (username, password) => {
    // Single-login intelligente: prova admin, se 401 fallback a reseller.
    let res;
    let detectedRole = "admin";
    try {
      res = await axios.post(`${API}/admin/login`, { username, password });
    } catch (e1) {
      if (e1.response?.status === 401) {
        try {
          res = await axios.post(`${API}/reseller/login`, { username, password });
          detectedRole = "reseller";
        } catch (e2) {
          throw e2;
        }
      } else {
        throw e1;
      }
    }
    const apiRole = res.data.role || detectedRole;
    setToken(res.data.access_token || res.data.token);
    setRole(apiRole);
    try {
      if (res.data.refresh_token) {
        localStorage.setItem("sktv_admin_refresh", res.data.refresh_token);
      }
    } catch (e) { console.warn("persist refresh token fallita", e); }
    setAdmin({ username: res.data.username, role: apiRole });
    return { ...res.data, role: apiRole };
  };

  const logout = () => {
    setToken(null);
    setAdmin(null);
    setRole("admin");
    try { localStorage.removeItem("sktv_admin_refresh"); } catch (e) { console.warn("logout cleanup fallita", e); }
  };

  return (
    <AuthContext.Provider value={{
      token, admin, role, login, logout, isAuthenticated: !!token
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Toast Component
const Toast = ({ message, type = "success", onClose }) => (
  <motion.div
    initial={{ opacity: 0, y: 50 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 50 }}
    className={`fixed bottom-4 right-4 px-6 py-3 rounded-sm shadow-lg z-50 flex items-center gap-3
      ${type === "success" ? "bg-green-600" : type === "error" ? "bg-netflix-red" : "bg-zinc-800"}`}
  >
    {type === "success" ? <Check className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
    <span className="font-medium">{message}</span>
    <button onClick={onClose} className="ml-2 hover:opacity-70"><X className="w-4 h-4" /></button>
  </motion.div>
);

// Login Page
const LoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [setupMode, setSetupMode] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSetup = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/setup`);
      setError(`Admin creato! Username: ${res.data.username}, Password: ${res.data.default_password}`);
      setSetupMode(false);
    } catch (err) {
      setError(err.response?.data?.detail || "Setup fallito");
    }
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(username, password);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Login fallito");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center relative overflow-hidden">
      {/* Background */}
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-30"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1740030325906-01bd0b632cdb?w=1920')" }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-transparent" />
      
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative z-10 w-full max-w-md p-8"
      >
        <div className="glass rounded-lg p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <img src="/logo.png" alt="SK TV" className="h-28 w-auto mx-auto mb-3 drop-shadow-[0_0_24px_rgba(229,9,20,0.35)]" />
            <h1 className="font-headings text-5xl text-netflix-red tracking-wider">SK TV</h1>
            <p className="text-zinc-400 mt-2">Admin Panel</p>
          </div>

          {error && (
            <div className={`mb-4 p-3 rounded-sm text-sm ${error.includes("creato") ? "bg-green-900/50 border border-green-700" : "bg-red-900/50 border border-red-700"}`}>
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Username</label>
              <input
                data-testid="login-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red focus:ring-1 focus:ring-netflix-red outline-none transition-colors"
                placeholder="admin"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Password</label>
              <div className="relative">
                <input
                  data-testid="login-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red focus:ring-1 focus:ring-netflix-red outline-none transition-colors pr-12"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-netflix-red hover:bg-red-700 text-white font-bold rounded-sm px-6 py-3 uppercase tracking-wide transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock className="w-5 h-5" />}
              {loading ? "Accesso..." : "Accedi"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              data-testid="setup-admin-btn"
              onClick={handleSetup}
              disabled={loading}
              className="text-zinc-500 hover:text-zinc-300 text-sm underline"
            >
              Prima volta? Crea admin
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Sidebar Component
const Sidebar = ({ activeTab, setActiveTab, mobileOpen = false, onCloseMobile = () => {} }) => {
  const { logout, admin } = useAuth();
  const navigate = useNavigate();

  const menuItems = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "dns", label: "DNS Servers", icon: Globe },
    { id: "resellers", label: "Reseller", icon: Store },
    { id: "users", label: "Utenti", icon: Users },
    { id: "stats", label: "Statistiche", icon: BarChart3 },
    { id: "bandwidth", label: "Monitor Banda", icon: Gauge },
    { id: "connections", label: "Connessioni", icon: Activity },
    { id: "diagnostics", label: "Diagnostica Player", icon: Stethoscope },
    { id: "messages", label: "Messaggi", icon: MessageSquare },
    { id: "app-config", label: "Configurazione App", icon: Server },
    { id: "settings", label: "Impostazioni", icon: Settings },
  ];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className={`w-64 bg-zinc-950 border-r border-zinc-800 h-screen fixed left-0 top-0 flex flex-col z-50 transition-transform duration-200 md:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}>
      {/* Logo */}
      <div className="p-6 border-b border-zinc-800 relative">
        <button
          data-testid="sidebar-close-btn"
          onClick={onCloseMobile}
          className="md:hidden absolute top-4 right-4 text-zinc-400 hover:text-white"
        >
          <X className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="SK TV" className="h-10 w-auto" />
          <h1 className="font-headings text-4xl text-netflix-red tracking-wider">SK TV</h1>
        </div>
        <p className="text-zinc-500 text-sm mt-1">Admin Panel</p>
      </div>

      {/* Menu */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {menuItems.map((item) => (
          <button
            key={item.id}
            data-testid={`nav-${item.id}`}
            onClick={() => { setActiveTab(item.id); onCloseMobile(); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all
              ${activeTab === item.id 
                ? "bg-netflix-red/20 text-netflix-red border-l-4 border-netflix-red" 
                : "text-zinc-400 hover:text-white hover:bg-zinc-900"}`}
          >
            <item.icon className="w-5 h-5" />
            <span className="font-medium">{item.label}</span>
          </button>
        ))}
      </nav>

      {/* User */}
      <div className="p-4 border-t border-zinc-800">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-netflix-red rounded-full flex items-center justify-center">
            <span className="font-bold text-lg">{admin?.username?.[0]?.toUpperCase() || "A"}</span>
          </div>
          <div>
            <p className="font-medium">{admin?.username || "Admin"}</p>
            <p className="text-xs text-zinc-500">Amministratore</p>
          </div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-zinc-900 hover:bg-zinc-800 rounded-sm text-zinc-400 hover:text-white transition-colors"
        >
          <LogOut className="w-4 h-4" />
          <span>Esci</span>
        </button>
      </div>
    </div>
  );
};

// Dashboard Page
const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(`${API}/dashboard/stats`);
        setStats(res.data);
      } catch (err) {
        console.error("dashboard fetch failed", err);
        setError(err.response?.data?.error || err.response?.data?.detail || err.message || "Errore caricamento statistiche");
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const statCards = stats ? [
    { label: "Utenti Totali", value: stats.total_users, icon: Users, color: "text-blue-400" },
    { label: "Utenti Attivi", value: stats.active_users, icon: Activity, color: "text-green-400" },
    { label: "Dispositivi", value: stats.total_devices, icon: Server, color: "text-purple-400" },
    { label: "DNS Servers", value: stats.total_dns, icon: Globe, color: "text-yellow-400" },
    { label: "In Scadenza", value: stats.expiring_soon, icon: AlertTriangle, color: "text-netflix-red" },
    { label: "Messaggi Recenti", value: stats.recent_messages, icon: MessageSquare, color: "text-cyan-400" },
  ] : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <Loader2 className="w-8 h-8 animate-spin text-netflix-red" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="animate-fade-in">
        <h2 className="font-headings text-4xl mb-6">Dashboard</h2>
        <div data-testid="dashboard-error" className="bg-red-900/30 border border-red-700 text-red-300 p-6 rounded-lg max-w-2xl">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold">Impossibile caricare le statistiche</p>
              <p className="text-sm mt-1 opacity-90">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-3 px-4 py-2 bg-netflix-red hover:bg-red-700 rounded-sm text-white text-sm font-medium"
              >
                Riprova
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="dashboard-page">
      <h2 className="font-headings text-4xl mb-6">Dashboard</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map((card, idx) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
            className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-lg hover:border-zinc-700 transition-colors"
            data-testid={`stat-card-${card.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-sm">{card.label}</p>
                <p className="text-4xl font-bold mt-2">{card.value ?? 0}</p>
              </div>
              <card.icon className={`w-10 h-10 ${card.color}`} />
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// DNS Management Page
const DNSPage = () => {
  const [dnsList, setDnsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDns, setEditingDns] = useState(null);
  const [formData, setFormData] = useState({ name: "", url: "" });
  const [toast, setToast] = useState(null);

  const fetchDns = async () => {
    try {
      const res = await axios.get(`${API}/dns`);
      setDnsList(res.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchDns(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingDns) {
        await axios.put(`${API}/dns/${editingDns.id}`, formData);
        setToast({ message: "DNS aggiornato!", type: "success" });
      } else {
        await axios.post(`${API}/dns`, formData);
        setToast({ message: "DNS creato!", type: "success" });
      }
      setShowModal(false);
      setFormData({ name: "", url: "" });
      setEditingDns(null);
      fetchDns();
    } catch (err) {
      setToast({ message: err.response?.data?.detail || "Errore", type: "error" });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo DNS?")) return;
    try {
      await axios.delete(`${API}/dns/${id}`);
      setToast({ message: "DNS eliminato!", type: "success" });
      fetchDns();
    } catch (err) {
      setToast({ message: err.response?.data?.detail || "Errore", type: "error" });
    }
  };

  const openEdit = (dns) => {
    setEditingDns(dns);
    setFormData({ name: dns.name, url: dns.url });
    setShowModal(true);
  };

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-headings text-4xl">DNS Servers</h2>
        <button
          data-testid="add-dns-btn"
          onClick={() => { setEditingDns(null); setFormData({ name: "", url: "" }); setShowModal(true); }}
          className="bg-netflix-red hover:bg-red-700 text-white font-bold rounded-sm px-4 py-2 flex items-center gap-2 transition-all active:scale-95"
        >
          <Plus className="w-5 h-5" />
          Aggiungi DNS
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-netflix-red" />
        </div>
      ) : dnsList.length === 0 ? (
        <div className="text-center py-12 text-zinc-400">
          <Globe className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>Nessun DNS configurato</p>
          <p className="text-sm">Aggiungi il tuo primo DNS server</p>
        </div>
      ) : (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-hidden overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>URL</th>
                <th>Utenti</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {dnsList.map((dns) => (
                <tr key={dns.id}>
                  <td className="font-medium text-white">{dns.name}</td>
                  <td className="font-mono text-sm">{dns.url}</td>
                  <td>
                    <span className="bg-zinc-800 px-2 py-1 rounded text-xs">{dns.users_count} utenti</span>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button
                        data-testid={`edit-dns-${dns.id}`}
                        onClick={() => openEdit(dns)}
                        className="p-2 hover:bg-zinc-800 rounded text-zinc-400 hover:text-white transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`delete-dns-${dns.id}`}
                        onClick={() => handleDelete(dns.id)}
                        className="p-2 hover:bg-red-900/50 rounded text-zinc-400 hover:text-netflix-red transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-zinc-900 border border-zinc-700 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
            >
              <h3 className="font-headings text-2xl mb-4">{editingDns ? "Modifica DNS" : "Nuovo DNS"}</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Nome</label>
                  <input
                    data-testid="dns-name-input"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
                    placeholder="Server Principale"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">URL</label>
                  <input
                    data-testid="dns-url-input"
                    type="url"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none font-mono text-sm"
                    placeholder="http://server.com:8080"
                    required
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white rounded-sm px-4 py-2 transition-colors"
                  >
                    Annulla
                  </button>
                  <button
                    data-testid="dns-submit-btn"
                    type="submit"
                    className="flex-1 bg-netflix-red hover:bg-red-700 text-white font-bold rounded-sm px-4 py-2 transition-all active:scale-95"
                  >
                    {editingDns ? "Salva" : "Crea"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      </AnimatePresence>
    </div>
  );
};

// Users Management Page
const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [dnsList, setDnsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDevicesModal, setShowDevicesModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [devices, setDevices] = useState([]);
  const [formData, setFormData] = useState({
    username: "", password: "", xtream_username: "", xtream_password: "", dns_id: ""
  });
  const [editFormData, setEditFormData] = useState({
    username: "", password: "", xtream_username: "", xtream_password: "", dns_id: "", expiration_date: "", is_active: true
  });
  const [toast, setToast] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchData = async () => {
    try {
      const [usersRes, dnsRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/dns`)
      ]);
      // Backend v2 ritorna {total, items}; manteniamo retrocompat se un giorno tornasse array
      const usersList = Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data?.items || []);
      setUsers(usersList);
      setDnsList(dnsRes.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let payload = { ...formData };
      const useCode = formData.accessMode === 'code';
      if (useCode) {
        const code = (formData.device_code || '').trim().toUpperCase();
        if (!/^[A-Z0-9\-]{6,64}$/.test(code)) {
          setToast({ message: "Codice non valido (A-Z 0-9 '-', 6-64 caratteri)", type: "error" });
          return;
        }
        // Username = etichetta cliente se fornita, altrimenti il codice stesso (slugged)
        const label = (formData.client_label || '').trim();
        const usernameBase = label || code;
        payload.username = usernameBase
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '')
          .slice(0, 64) || `dev_${code.toLowerCase().replace(/[^a-z0-9]/g, '')}`;
        // Password random per soddisfare il backend (l'utente non la userà mai)
        payload.password = Math.random().toString(36).slice(2, 14) + Math.random().toString(36).slice(2, 14);
      }
      delete payload.accessMode;
      delete payload.device_code;
      delete payload.client_label;

      const res = await axios.post(`${API}/users`, payload);
      const newUser = res.data;

      if (useCode && newUser?.id) {
        const code = formData.device_code.trim().toUpperCase();
        try {
          await axios.post(`${API}/users/${newUser.id}/device-codes`, { device_code: code });
          setToast({ message: `Utente creato con codice ${code}`, type: "success" });
        } catch (err2) {
          const detail = err2.response?.data?.detail
            || err2.response?.data?.error
            || err2.response?.statusText
            || err2.message
            || `HTTP ${err2.response?.status || '?'}`;
          setToast({
            message: `Utente creato MA codice non assegnato: ${detail}. Verifica che il backend sul VPS sia aggiornato.`,
            type: "error"
          });
        }
      } else {
        setToast({ message: "Utente creato!", type: "success" });
      }

      setShowModal(false);
      setFormData({ username: "", password: "", xtream_username: "", xtream_password: "", dns_id: "", accessMode: "username", device_code: "" });
      fetchData();
    } catch (err) {
      setToast({ message: err.response?.data?.detail || "Errore", type: "error" });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo utente e tutti i suoi dispositivi?")) return;
    try {
      await axios.delete(`${API}/users/${id}`);
      setToast({ message: "Utente eliminato!", type: "success" });
      fetchData();
    } catch (err) {
      setToast({ message: err.response?.data?.detail || "Errore", type: "error" });
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await axios.put(`${API}/users/${user.id}`, { is_active: !user.is_active });
      setToast({ message: user.is_active ? "Utente disattivato" : "Utente attivato", type: "success" });
      fetchData();
    } catch (err) {
      setToast({ message: err.response?.data?.detail || "Errore", type: "error" });
    }
  };

  const handleChangeDns = async (userId, newDnsId) => {
    try {
      await axios.put(`${API}/users/${userId}`, { dns_id: newDnsId });
      setToast({ message: "DNS aggiornato!", type: "success" });
      fetchData();
    } catch (err) {
      setToast({ message: err.response?.data?.detail || "Errore", type: "error" });
    }
  };

  const viewDevices = async (user) => {
    setSelectedUser(user);
    try {
      const res = await axios.get(`${API}/users/${user.id}/devices`);
      setDevices(res.data);
      setShowDevicesModal(true);
    } catch (err) {
      setToast({ message: "Errore nel caricamento dispositivi", type: "error" });
    }
  };

  const removeDevice = async (deviceId) => {
    if (!window.confirm("Rimuovere questo dispositivo?")) return;
    try {
      await axios.delete(`${API}/users/${selectedUser.id}/devices/${deviceId}`);
      setToast({ message: "Dispositivo rimosso!", type: "success" });
      const res = await axios.get(`${API}/users/${selectedUser.id}/devices`);
      setDevices(res.data);
      fetchData();
    } catch (err) {
      setToast({ message: err.response?.data?.detail || "Errore", type: "error" });
    }
  };

  const forceLogoutDevice = async (deviceId) => {
    if (!window.confirm("Forzare il logout di questo dispositivo?\nL'utente dovrà rifare login dall'app.")) return;
    try {
      await axios.post(`${API}/users/${selectedUser.id}/devices/${deviceId}/force-logout`);
      setToast({ message: "Logout forzato eseguito!", type: "success" });
      const res = await axios.get(`${API}/users/${selectedUser.id}/devices`);
      setDevices(res.data);
      fetchData();
    } catch (err) {
      setToast({ message: err.response?.data?.detail || "Errore", type: "error" });
    }
  };

  // ==================== Device Codes (login alternativo via codice) ====================
  const [newDeviceCode, setNewDeviceCode] = useState("");

  const addDeviceCode = async () => {
    const code = newDeviceCode.trim().toUpperCase();
    if (!code) return;
    if (!/^[A-Z0-9\-]{6,64}$/.test(code)) {
      setToast({ message: "Formato codice non valido (A-Z, 0-9, '-', 6-64 caratteri)", type: "error" });
      return;
    }
    try {
      const res = await axios.post(`${API}/users/${selectedUser.id}/device-codes`, { device_code: code });
      setSelectedUser({ ...selectedUser, device_codes: res.data.device_codes });
      setNewDeviceCode("");
      setToast({ message: "Codice attivato", type: "success" });
      fetchData();
    } catch (err) {
      setToast({ message: err.response?.data?.detail || "Errore", type: "error" });
    }
  };

  const removeDeviceCode = async (code) => {
    if (!window.confirm(`Revocare il codice "${code}"?\nL'app collegata non potrà più fare login automatico.`)) return;
    try {
      const res = await axios.delete(`${API}/users/${selectedUser.id}/device-codes/${encodeURIComponent(code)}`);
      setSelectedUser({ ...selectedUser, device_codes: res.data.device_codes });
      setToast({ message: "Codice revocato", type: "success" });
      fetchData();
    } catch (err) {
      setToast({ message: err.response?.data?.detail || "Errore", type: "error" });
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditFormData({
      username: user.username,
      password: "",
      xtream_username: "",
      xtream_password: "",
      dns_id: user.dns_id,
      expiration_date: user.expiration_date ? user.expiration_date.slice(0, 10) : "",
      is_active: user.is_active !== false,
      force_proxy: user.force_proxy === true,
      label: user.label || ""
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const updateData = {};
      if (editFormData.username && editFormData.username !== selectedUser.username) {
        updateData.username = editFormData.username;
      }
      if (editFormData.password) {
        updateData.password = editFormData.password;
      }
      if (editFormData.xtream_username) {
        updateData.xtream_username = editFormData.xtream_username;
      }
      if (editFormData.xtream_password) {
        updateData.xtream_password = editFormData.xtream_password;
      }
      if (editFormData.dns_id !== selectedUser.dns_id) {
        updateData.dns_id = editFormData.dns_id;
      }
      // Scadenza: invia se modificata (stringa vuota = rimozione scadenza → null)
      const currentExp = selectedUser.expiration_date ? selectedUser.expiration_date.slice(0, 10) : "";
      if (editFormData.expiration_date !== currentExp) {
        updateData.expiration_date = editFormData.expiration_date || null;
      }
      // Stato attivo/sospeso
      if (editFormData.is_active !== (selectedUser.is_active !== false)) {
        updateData.is_active = editFormData.is_active;
      }
      // Instradamento stream (diretto vs proxy VPS)
      if (editFormData.force_proxy !== (selectedUser.force_proxy === true)) {
        updateData.force_proxy = editFormData.force_proxy;
      }
      // Etichetta cliente
      if ((editFormData.label || "") !== (selectedUser.label || "")) {
        updateData.label = editFormData.label || "";
      }

      if (Object.keys(updateData).length === 0) {
        setToast({ message: "Nessuna modifica effettuata", type: "info" });
        setShowEditModal(false);
        return;
      }

      await axios.put(`${API}/users/${selectedUser.id}`, updateData);
      setToast({ message: "Utente aggiornato!", type: "success" });
      setShowEditModal(false);
      fetchData();
    } catch (err) {
      setToast({ message: err.response?.data?.detail || "Errore", type: "error" });
    }
  };

  const filteredUsers = users.filter(u => {
    const q = searchTerm.toLowerCase().trim();
    if (!q) return true;
    if (u.username?.toLowerCase().includes(q)) return true;
    if (u.label?.toLowerCase().includes(q)) return true;
    if (Array.isArray(u.device_codes) && u.device_codes.some(c => c.toLowerCase().includes(q))) return true;
    return false;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const isExpiringSoon = (dateStr) => {
    if (!dateStr) return false;
    const expDate = new Date(dateStr);
    const now = new Date();
    const diffDays = (expDate - now) / (1000 * 60 * 60 * 24);
    return diffDays <= 3 && diffDays > 0;
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-headings text-4xl">Utenti</h2>
        <div className="flex gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <input
              data-testid="search-users"
              type="text"
              placeholder="Cerca per username o codice..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 rounded-sm pl-10 pr-4 py-2 text-white focus:border-netflix-red outline-none w-40 md:w-64"
            />
          </div>
          <button
            data-testid="add-user-btn"
            onClick={() => setShowModal(true)}
            className="bg-netflix-red hover:bg-red-700 text-white font-bold rounded-sm px-4 py-2 flex items-center gap-2 transition-all active:scale-95"
          >
            <Plus className="w-5 h-5" />
            Nuovo Utente
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-netflix-red" />
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-12 text-zinc-400">
          <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>{searchTerm ? "Nessun utente trovato" : "Nessun utente creato"}</p>
        </div>
      ) : (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-hidden overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>DNS</th>
                <th>Dispositivi</th>
                <th>Scadenza</th>
                <th>Stato</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td className="font-medium text-white">
                    {user.username}
                    {user.label && <div className="text-xs text-zinc-400 font-normal mt-0.5">{user.label}</div>}
                  </td>
                  <td>
                    <select
                      data-testid={`user-dns-select-${user.id}`}
                      value={user.dns_id}
                      onChange={(e) => handleChangeDns(user.id, e.target.value)}
                      className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-sm text-white"
                    >
                      {dnsList.map(dns => (
                        <option key={dns.id} value={dns.id}>{dns.name}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button
                      data-testid={`view-devices-${user.id}`}
                      onClick={() => viewDevices(user)}
                      className="bg-zinc-800 hover:bg-zinc-700 px-3 py-1 rounded text-sm flex items-center gap-1"
                    >
                      <Server className="w-4 h-4" />
                      {user.devices_count}/{user.max_devices}
                    </button>
                  </td>
                  <td>
                    <span className={`px-2 py-1 rounded text-xs ${
                      isExpired(user.expiration_date) ? "bg-red-900/50 text-red-400" :
                      isExpiringSoon(user.expiration_date) ? "bg-yellow-900/50 text-yellow-400" :
                      "bg-zinc-800 text-zinc-300"
                    }`}>
                      {formatDate(user.expiration_date)}
                    </span>
                  </td>
                  <td>
                    <button
                      data-testid={`toggle-user-${user.id}`}
                      onClick={() => handleToggleActive(user)}
                      className={`px-3 py-1 rounded text-xs font-medium ${
                        user.is_active ? "bg-green-900/50 text-green-400" : "bg-red-900/50 text-red-400"
                      }`}
                    >
                      {user.is_active ? "Attivo" : "Disattivato"}
                    </button>
                    {user.force_proxy === true && (
                      <span data-testid={`user-vps-badge-${user.id}`} className="ml-1 px-2 py-1 rounded text-xs bg-blue-900/50 text-blue-400" title="Stream instradato via VPS">
                        VPS
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="flex gap-1">
                      <button
                        data-testid={`edit-user-${user.id}`}
                        onClick={() => openEditModal(user)}
                        className="p-2 hover:bg-zinc-800 rounded text-zinc-400 hover:text-white transition-colors"
                        title="Modifica utente"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`delete-user-${user.id}`}
                        onClick={() => handleDelete(user.id)}
                        className="p-2 hover:bg-red-900/50 rounded text-zinc-400 hover:text-netflix-red transition-colors"
                        title="Elimina utente"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create User Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-zinc-900 border border-zinc-700 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
            >
              <h3 className="font-headings text-2xl mb-4">Nuovo Utente</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Modalità di accesso */}
                <div className="bg-zinc-800/40 border border-zinc-700 rounded p-3">
                  <p className="text-xs text-zinc-400 mb-2">Modalità di accesso app</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      data-testid="user-mode-username"
                      onClick={() => setFormData({ ...formData, accessMode: 'username' })}
                      className={`flex-1 px-3 py-2 rounded-sm text-sm font-semibold transition-colors ${
                        (formData.accessMode || 'username') === 'username'
                          ? 'bg-netflix-red text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:text-white'
                      }`}
                    >
                      Username + Password
                    </button>
                    <button
                      type="button"
                      data-testid="user-mode-code"
                      onClick={() => setFormData({ ...formData, accessMode: 'code' })}
                      className={`flex-1 px-3 py-2 rounded-sm text-sm font-semibold transition-colors ${
                        formData.accessMode === 'code'
                          ? 'bg-netflix-red text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:text-white'
                      }`}
                    >
                      Codice dispositivo
                    </button>
                  </div>
                </div>

                {formData.accessMode === 'code' ? (
                  <>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">
                        Codice dispositivo (fornito dall'app del cliente, es. SKTV-AB12-CD34)
                      </label>
                      <input
                        data-testid="user-device-code-input"
                        type="text"
                        value={formData.device_code || ''}
                        onChange={(e) => setFormData({ ...formData, device_code: e.target.value.toUpperCase() })}
                        placeholder="SKTV-AB12-CD34"
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 font-mono uppercase tracking-wider text-white focus:border-netflix-red outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">
                        Etichetta cliente (opzionale, mostrata nel pannello)
                      </label>
                      <input
                        data-testid="user-client-label-input"
                        type="text"
                        value={formData.client_label || ''}
                        onChange={(e) => setFormData({ ...formData, client_label: e.target.value })}
                        placeholder="Es. Mario Rossi - TV Salotto"
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
                      />
                      <p className="text-xs text-zinc-500 mt-1">
                        Se vuoto, useremo il codice come identificativo nel pannello.
                      </p>
                    </div>
                  </>
                ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Username App</label>
                    <input
                      data-testid="user-username-input"
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
                      required={formData.accessMode !== 'code'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Password App</label>
                    <input
                      data-testid="user-password-input"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
                      required={formData.accessMode !== 'code'}
                    />
                  </div>
                </div>
                )}
                
                <div className="border-t border-zinc-700 pt-4">
                  <p className="text-xs text-zinc-500 mb-3">Credenziali Xtream (verranno criptate)</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Username Xtream</label>
                      <input
                        data-testid="user-xtream-username-input"
                        type="text"
                        value={formData.xtream_username}
                        onChange={(e) => setFormData({ ...formData, xtream_username: e.target.value })}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Password Xtream</label>
                      <input
                        data-testid="user-xtream-password-input"
                        type="password"
                        value={formData.xtream_password}
                        onChange={(e) => setFormData({ ...formData, xtream_password: e.target.value })}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-zinc-400 mb-2">DNS Server</label>
                  <select
                    data-testid="user-dns-input"
                    value={formData.dns_id}
                    onChange={(e) => setFormData({ ...formData, dns_id: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
                    required
                  >
                    <option value="">Seleziona DNS</option>
                    {dnsList.map(dns => (
                      <option key={dns.id} value={dns.id}>{dns.name} - {dns.url}</option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white rounded-sm px-4 py-2 transition-colors"
                  >
                    Annulla
                  </button>
                  <button
                    data-testid="user-submit-btn"
                    type="submit"
                    className="flex-1 bg-netflix-red hover:bg-red-700 text-white font-bold rounded-sm px-4 py-2 transition-all active:scale-95"
                  >
                    Crea Utente
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Devices Modal */}
      <AnimatePresence>
        {showDevicesModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setShowDevicesModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-zinc-900 border border-zinc-700 rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <h3 className="font-headings text-2xl mb-4">
                Dispositivi di {selectedUser?.username}
              </h3>

              {/* Device codes (login alternativo) */}
              <div className="mb-5 bg-zinc-800/60 border border-zinc-700 rounded p-3">
                <p className="text-xs uppercase tracking-wider text-zinc-400 mb-2">
                  🔑 Codici dispositivo (login senza username/password)
                </p>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newDeviceCode}
                    onChange={(e) => setNewDeviceCode(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') addDeviceCode(); }}
                    placeholder="Es. SKTV-AB12-CD34"
                    data-testid="device-code-input"
                    className="flex-1 bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm font-mono uppercase tracking-wider text-white placeholder-zinc-500 focus:outline-none focus:border-netflix-red"
                  />
                  <button
                    onClick={addDeviceCode}
                    data-testid="device-code-add-btn"
                    className="bg-netflix-red hover:bg-red-700 text-white px-4 py-2 rounded text-sm font-semibold transition-colors"
                  >
                    Attiva
                  </button>
                </div>
                {(selectedUser?.device_codes?.length ?? 0) === 0 ? (
                  <p className="text-xs text-zinc-500 italic">Nessun codice attivo</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {selectedUser.device_codes.map((c) => (
                      <span key={c} className="inline-flex items-center gap-2 bg-zinc-900 border border-zinc-700 rounded-full pl-3 pr-1 py-1 text-xs font-mono">
                        {c}
                        <button
                          onClick={() => removeDeviceCode(c)}
                          data-testid={`device-code-remove-${c}`}
                          className="text-zinc-500 hover:text-netflix-red rounded-full p-1"
                          title="Revoca codice"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {devices.length === 0 ? (
                <p className="text-zinc-400 text-center py-8">Nessun dispositivo registrato</p>
              ) : (
                <div className="space-y-2">
                  {devices.map((device) => (
                    <div key={device.id} className="flex items-center justify-between bg-zinc-800 p-3 rounded">
                      <div>
                        <p className="font-mono text-sm text-white">{device.device_id.substring(0, 20)}...</p>
                        <p className="text-xs text-zinc-500">
                          Ultimo accesso: {formatDate(device.last_active)}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          data-testid={`force-logout-device-${device.device_id}`}
                          onClick={() => forceLogoutDevice(device.device_id)}
                          title="Forza logout del dispositivo"
                          className="p-2 hover:bg-yellow-900/50 rounded text-zinc-400 hover:text-yellow-400"
                        >
                          <LogOut className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`remove-device-${device.device_id}`}
                          onClick={() => removeDevice(device.device_id)}
                          title="Rimuovi dispositivo"
                          className="p-2 hover:bg-red-900/50 rounded text-zinc-400 hover:text-netflix-red"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <button
                onClick={() => setShowDevicesModal(false)}
                className="w-full mt-4 bg-zinc-800 hover:bg-zinc-700 text-white rounded-sm px-4 py-2"
              >
                Chiudi
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit User Modal */}
      <AnimatePresence>
        {showEditModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setShowEditModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-zinc-900 border border-zinc-700 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
            >
              <h3 className="font-headings text-2xl mb-4">Modifica Utente</h3>
              <form onSubmit={handleEditSubmit} className="space-y-4">
                {/* App Credentials */}
                <div className="border-b border-zinc-700 pb-4">
                  <p className="text-xs text-zinc-500 mb-3">Credenziali App (lascia vuoto per non modificare)</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Username App</label>
                      <input
                        data-testid="edit-user-username"
                        type="text"
                        value={editFormData.username}
                        onChange={(e) => setEditFormData({ ...editFormData, username: e.target.value })}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Nuova Password</label>
                      <input
                        data-testid="edit-user-password"
                        type="password"
                        value={editFormData.password}
                        onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                        placeholder="••••••••"
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Xtream Credentials */}
                <div className="border-b border-zinc-700 pb-4">
                  <p className="text-xs text-netflix-red mb-3">
                    ⚠️ Credenziali Xtream - MAI visibili, solo riscrivibili
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Nuovo Username Xtream</label>
                      <input
                        data-testid="edit-xtream-username"
                        type="text"
                        value={editFormData.xtream_username}
                        onChange={(e) => setEditFormData({ ...editFormData, xtream_username: e.target.value })}
                        placeholder="Lascia vuoto per mantenere"
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Nuova Password Xtream</label>
                      <input
                        data-testid="edit-xtream-password"
                        type="password"
                        value={editFormData.xtream_password}
                        onChange={(e) => setEditFormData({ ...editFormData, xtream_password: e.target.value })}
                        placeholder="Lascia vuoto per mantenere"
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
                      />
                    </div>
                  </div>
                </div>

                {/* DNS Selection */}
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">DNS Server</label>
                  <select
                    data-testid="edit-user-dns"
                    value={editFormData.dns_id}
                    onChange={(e) => setEditFormData({ ...editFormData, dns_id: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
                  >
                    {dnsList.map(dns => (
                      <option key={dns.id} value={dns.id}>{dns.name} - {dns.url}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Data scadenza</label>
                    <input
                      data-testid="edit-user-expiration"
                      type="date"
                      value={editFormData.expiration_date}
                      onChange={(e) => setEditFormData({ ...editFormData, expiration_date: e.target.value })}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
                    />
                    <p className="text-xs text-zinc-500 mt-1">Vuoto = nessuna scadenza</p>
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Stato account</label>
                    <button
                      type="button"
                      data-testid="edit-user-active-toggle"
                      onClick={() => setEditFormData({ ...editFormData, is_active: !editFormData.is_active })}
                      className={`w-full px-4 py-3 rounded-sm font-semibold transition-colors ${
                        editFormData.is_active
                          ? 'bg-green-700/30 border border-green-600 text-green-400 hover:bg-green-700/50'
                          : 'bg-red-700/30 border border-red-600 text-red-400 hover:bg-red-700/50'
                      }`}
                    >
                      {editFormData.is_active ? '✓ Attivo' : '⏸ Sospeso'}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Etichetta cliente (visibile solo nel pannello)</label>
                  <input
                    data-testid="edit-user-label"
                    type="text"
                    value={editFormData.label || ""}
                    onChange={(e) => setEditFormData({ ...editFormData, label: e.target.value })}
                    placeholder="Es. Mario Rossi - TV Salotto"
                    maxLength={120}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
                  />
                  <p className="text-xs text-zinc-400 mt-1">Usala per riconoscere e cercare il cliente al volo</p>
                </div>

                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Instradamento stream</label>
                  <button
                    type="button"
                    data-testid="edit-user-force-proxy-toggle"
                    onClick={() => setEditFormData({ ...editFormData, force_proxy: !editFormData.force_proxy })}
                    className={`w-full px-4 py-3 rounded-sm font-semibold transition-colors ${
                      editFormData.force_proxy
                        ? 'bg-blue-700/30 border border-blue-600 text-blue-400 hover:bg-blue-700/50'
                        : 'bg-zinc-800 border border-zinc-700 text-zinc-300 hover:bg-zinc-700'
                    }`}
                  >
                    {editFormData.force_proxy ? 'Via server VPS (cifrato)' : 'Diretto al provider (default)'}
                  </button>
                  <p className="text-xs text-zinc-400 mt-1">
                    Attiva SOLO se l'ISP del cliente blocca il provider: lo stream passerà cifrato dalla VPS (consuma banda della VPS)
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white rounded-sm px-4 py-2 transition-colors"
                  >
                    Annulla
                  </button>
                  <button
                    data-testid="edit-user-submit"
                    type="submit"
                    className="flex-1 bg-netflix-red hover:bg-red-700 text-white font-bold rounded-sm px-4 py-2 transition-all active:scale-95"
                  >
                    Salva Modifiche
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      </AnimatePresence>
    </div>
  );
};

// Messages Page
const MessagesPage = () => {
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ title: "", content: "", user_id: "" });
  const [toast, setToast] = useState(null);

  const fetchData = async () => {
    try {
      const [messagesRes, usersRes] = await Promise.all([
        axios.get(`${API}/messages`),
        axios.get(`${API}/users`)
      ]);
      setMessages(messagesRes.data);
      const usersList = Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data?.items || []);
      setUsers(usersList);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData };
      if (data.user_id === "") data.user_id = null;
      await axios.post(`${API}/messages`, data);
      setToast({ message: "Messaggio inviato!", type: "success" });
      setShowModal(false);
      setFormData({ title: "", content: "", user_id: "" });
      fetchData();
    } catch (err) {
      setToast({ message: err.response?.data?.detail || "Errore", type: "error" });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo messaggio?")) return;
    try {
      await axios.delete(`${API}/messages/${id}`);
      setToast({ message: "Messaggio eliminato!", type: "success" });
      fetchData();
    } catch (err) {
      setToast({ message: err.response?.data?.detail || "Errore", type: "error" });
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  };

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-headings text-4xl">Messaggi</h2>
        <button
          data-testid="add-message-btn"
          onClick={() => setShowModal(true)}
          className="bg-netflix-red hover:bg-red-700 text-white font-bold rounded-sm px-4 py-2 flex items-center gap-2 transition-all active:scale-95"
        >
          <Plus className="w-5 h-5" />
          Nuovo Messaggio
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-netflix-red" />
        </div>
      ) : messages.length === 0 ? (
        <div className="text-center py-12 text-zinc-400">
          <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>Nessun messaggio</p>
        </div>
      ) : (
        <div className="space-y-4">
          {messages.map((msg) => (
            <motion.div
              key={msg.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4"
            >
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-bold text-white">{msg.title}</h4>
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      msg.is_broadcast ? "bg-blue-900/50 text-blue-400" : "bg-purple-900/50 text-purple-400"
                    }`}>
                      {msg.is_broadcast ? "Tutti" : "Singolo"}
                    </span>
                  </div>
                  <p className="text-zinc-400 text-sm">{msg.content}</p>
                  <p className="text-zinc-600 text-xs mt-2">{formatDate(msg.created_at)}</p>
                </div>
                <button
                  data-testid={`delete-message-${msg.id}`}
                  onClick={() => handleDelete(msg.id)}
                  className="p-2 hover:bg-red-900/50 rounded text-zinc-400 hover:text-netflix-red"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-zinc-900 border border-zinc-700 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
            >
              <h3 className="font-headings text-2xl mb-4">Nuovo Messaggio</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Titolo</label>
                  <input
                    data-testid="message-title-input"
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Contenuto</label>
                  <textarea
                    data-testid="message-content-input"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none h-32 resize-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Destinatario</label>
                  <select
                    data-testid="message-user-input"
                    value={formData.user_id}
                    onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
                  >
                    <option value="">Tutti gli utenti (Broadcast)</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>{user.username}</option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white rounded-sm px-4 py-2"
                  >
                    Annulla
                  </button>
                  <button
                    data-testid="message-submit-btn"
                    type="submit"
                    className="flex-1 bg-netflix-red hover:bg-red-700 text-white font-bold rounded-sm px-4 py-2 transition-all active:scale-95"
                  >
                    Invia
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      </AnimatePresence>
    </div>
  );
};

// Statistics Page
const StatsPage = () => {
  const [stats, setStats] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/stats/users`);
        setStats(res.data);
        setFetchError(false);
      } catch (err) {
        console.error("caricamento statistiche fallito", err);
        setFetchError(true);
      }
      setLoading(false);
    };
    fetchStats();
  }, []);

  const viewUserDetail = async (userId) => {
    try {
      const res = await axios.get(`${API}/stats/users/${userId}`);
      setUserDetail(res.data);
      setSelectedUser(userId);
    } catch (err) {
      console.error(err);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return "0m";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-netflix-red" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <h2 className="font-headings text-4xl mb-6">Statistiche Visione</h2>
      
      {fetchError ? (
        <div className="text-center py-12 text-amber-400" data-testid="stats-load-error">
          <BarChart3 className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>Impossibile caricare le statistiche. Ricarica la pagina o riprova più tardi.</p>
        </div>
      ) : stats.length === 0 ? (
        <div className="text-center py-12 text-zinc-400">
          <BarChart3 className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>Nessuna statistica disponibile</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Users Stats List */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6">
            <h3 className="font-headings text-xl mb-4">Utenti</h3>
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {stats.map((user) => (
                <div 
                  key={user.user_id}
                  data-testid={`stats-user-${user.username}`}
                  onClick={() => viewUserDetail(user.user_id)}
                  className={`p-4 rounded cursor-pointer transition-all ${
                    selectedUser === user.user_id 
                      ? "bg-netflix-red/20 border border-netflix-red" 
                      : "bg-zinc-800 hover:bg-zinc-700"
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-medium">{user.username}</span>
                    <span className="text-xs text-zinc-500">{formatDate(user.last_activity)}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-white">{user.total_views}</p>
                      <p className="text-xs text-zinc-500">Totali</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-red-400">{user.live_views}</p>
                      <p className="text-xs text-zinc-500">Live</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-blue-400">{user.movie_views}</p>
                      <p className="text-xs text-zinc-500">Film</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-purple-400">{user.series_views}</p>
                      <p className="text-xs text-zinc-500">Serie</p>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-xs text-zinc-400">
                    <Clock className="w-3 h-3" />
                    <span>Tempo totale: {formatTime(user.total_watch_time)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* User Detail */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6">
            <h3 className="font-headings text-xl mb-4">Dettaglio Utente</h3>
            {userDetail ? (
              <div data-testid="stats-user-detail">
                <div className="mb-4 p-4 bg-zinc-800 rounded">
                  <p className="text-lg font-bold">{userDetail.username || "-"}</p>
                  <p className="text-sm text-zinc-400">
                    Tipo preferito: <span className="text-red-400">{
                      (() => {
                        const counts = [
                          ["Live", userDetail.live_views || 0],
                          ["Film", userDetail.movie_views || 0],
                          ["Serie", userDetail.series_views || 0],
                        ].sort((a, b) => b[1] - a[1]);
                        return counts[0][1] > 0 ? counts[0][0] : "-";
                      })()
                    }</span>
                  </p>
                </div>

                <h4 className="text-sm text-zinc-400 mb-2 flex items-center gap-2">
                  <Play className="w-4 h-4" /> Cronologia recente
                </h4>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {(userDetail.history || userDetail.top_content || []).slice(0, 10).map((item, idx) => (
                    <div key={`${item.item_type}-${item.name}-${item.last_watched || idx}`} className="flex items-center gap-3 p-2 bg-zinc-800/50 rounded">
                      <span className={`text-xs px-2 py-1 rounded ${
                        item.item_type === "live" ? "bg-red-900/50 text-red-400" :
                        item.item_type === "movie" ? "bg-blue-900/50 text-blue-400" :
                        "bg-purple-900/50 text-purple-400"
                      }`}>
                        {item.item_type === "live" ? "LIVE" : item.item_type === "movie" ? "FILM" : "SERIE"}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{item.name}</p>
                        <p className="text-xs text-zinc-500">{formatDate(item.last_watched)}</p>
                      </div>
                      <span className="text-xs text-zinc-400">{formatTime(item.progress)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-zinc-500">
                <p>Seleziona un utente per vedere i dettagli</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Settings Page
const SettingsPage = () => {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/admin/change-password`, {
        old_password: oldPassword,
        new_password: newPassword,
      });
      setToast({ message: "Password cambiata!", type: "success" });
      setOldPassword("");
      setNewPassword("");
    } catch (err) {
      setToast({ message: err.response?.data?.error || err.response?.data?.detail || "Errore", type: "error" });
    }
    setLoading(false);
  };

  return (
    <div className="animate-fade-in">
      <h2 className="font-headings text-4xl mb-6">Impostazioni</h2>
      
      <div className="max-w-md">
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6">
          <h3 className="font-headings text-xl mb-4">Cambia Password</h3>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Password Attuale</label>
              <input
                data-testid="old-password-input"
                type="password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Nuova Password</label>
              <input
                data-testid="new-password-input"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
                required
              />
            </div>
            <button
              data-testid="change-password-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-netflix-red hover:bg-red-700 text-white font-bold rounded-sm px-4 py-3 transition-all active:scale-95 disabled:opacity-50"
            >
              {loading ? "Salvando..." : "Cambia Password"}
            </button>
          </form>
        </div>
      </div>

      <AnimatePresence>
        {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      </AnimatePresence>

      <TelegramSettings />
    </div>
  );
};

// ==================== Telegram Settings ====================
const TELEGRAM_EVENT_LABELS = {
  admin_login: "Login admin",
  user_new_device: "Utente da nuovo dispositivo",
  user_expiring: "Utente in scadenza",
  rate_limit: "Rate limit attivato",
  user_deleted: "Utente eliminato",
  xtream_error: "Errore provider Xtream",
};

const TelegramSettings = () => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [tokenDirty, setTokenDirty] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/app-config`);
        setCfg(res.data);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    })();
  }, []);

  const update = (k, v) => setCfg({ ...cfg, [k]: v });
  const updateEvent = (k, v) =>
    setCfg({ ...cfg, telegram_events: { ...(cfg.telegram_events || {}), [k]: v } });

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        telegram_enabled: !!cfg.telegram_enabled,
        telegram_chat_id: cfg.telegram_chat_id || "",
        telegram_events: cfg.telegram_events || {},
      };
      if (tokenDirty) payload.telegram_bot_token = cfg.telegram_bot_token || "";
      const res = await axios.put(`${API}/admin/app-config`, payload);
      setCfg(res.data);
      setTokenDirty(false);
      setToast({ message: "Configurazione Telegram salvata!", type: "success" });
    } catch (err) {
      setToast({ message: err.response?.data?.error || err.response?.data?.detail || "Errore", type: "error" });
    }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const payload = {};
      if (tokenDirty && cfg.telegram_bot_token) payload.bot_token = cfg.telegram_bot_token;
      if (cfg.telegram_chat_id) payload.chat_id = cfg.telegram_chat_id;
      await axios.post(`${API}/admin/telegram/test`, payload);
      setToast({ message: "Messaggio test inviato!", type: "success" });
    } catch (err) {
      setToast({ message: err.response?.data?.error || err.response?.data?.detail || "Test fallito", type: "error" });
    }
    setTesting(false);
  };

  if (loading || !cfg) {
    return <div className="mt-8"><Loader2 className="w-6 h-6 animate-spin text-netflix-red" /></div>;
  }

  return (
    <div className="max-w-xl mt-10" data-testid="telegram-settings">
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <Bell className="w-6 h-6 text-cyan-400" />
          <h3 className="font-headings text-xl">Notifiche Telegram</h3>
        </div>
        <p className="text-zinc-400 text-sm mb-5">
          Ricevi avvisi in tempo reale per eventi critici. Crea un bot con{" "}
          <span className="text-white font-mono">@BotFather</span>, poi scopri il tuo chat_id con{" "}
          <span className="text-white font-mono">@userinfobot</span>.
        </p>

        <form onSubmit={handleSave} className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              data-testid="tg-enabled"
              type="checkbox"
              checked={!!cfg.telegram_enabled}
              onChange={(e) => update("telegram_enabled", e.target.checked)}
              className="w-5 h-5 accent-netflix-red"
            />
            <span className="font-medium">Attiva notifiche Telegram</span>
          </label>

          <div>
            <label className="block text-sm text-zinc-400 mb-2">Bot Token</label>
            <input
              data-testid="tg-bot-token"
              type="password"
              value={cfg.telegram_bot_token || ""}
              onChange={(e) => {
                update("telegram_bot_token", e.target.value);
                setTokenDirty(true);
              }}
              placeholder={cfg.telegram_bot_token_set ? "(salvato — lascia vuoto per mantenere)" : "123456:ABCdef..."}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white font-mono text-sm focus:border-netflix-red outline-none"
            />
            {cfg.telegram_bot_token_set && !tokenDirty && (
              <p className="text-xs text-green-500 mt-1">Token attualmente salvato (nascosto per sicurezza)</p>
            )}
          </div>

          <div>
            <label className="block text-sm text-zinc-400 mb-2">Chat ID</label>
            <input
              data-testid="tg-chat-id"
              type="text"
              value={cfg.telegram_chat_id || ""}
              onChange={(e) => update("telegram_chat_id", e.target.value)}
              placeholder="123456789"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white font-mono text-sm focus:border-netflix-red outline-none"
            />
          </div>

          <div className="border-t border-zinc-800 pt-4">
            <p className="text-sm text-zinc-400 mb-3 font-medium">Eventi da notificare:</p>
            <div className="space-y-2">
              {Object.keys(TELEGRAM_EVENT_LABELS).map((ev) => (
                <label key={ev} className="flex items-center gap-3 cursor-pointer text-sm">
                  <input
                    data-testid={`tg-event-${ev}`}
                    type="checkbox"
                    checked={!!(cfg.telegram_events && cfg.telegram_events[ev])}
                    onChange={(e) => updateEvent(ev, e.target.checked)}
                    className="w-4 h-4 accent-netflix-red"
                  />
                  <span>{TELEGRAM_EVENT_LABELS[ev]}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              data-testid="tg-save-btn"
              type="submit"
              disabled={saving}
              className="flex-1 bg-netflix-red hover:bg-red-700 text-white font-bold rounded-sm px-4 py-3 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
              {saving ? "Salvando..." : "Salva"}
            </button>
            <button
              data-testid="tg-test-btn"
              type="button"
              onClick={handleTest}
              disabled={testing || !cfg.telegram_chat_id}
              className="flex-1 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-white font-medium rounded-sm px-4 py-3 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
              title={!cfg.telegram_chat_id ? "Inserisci chat_id prima di testare" : ""}
            >
              {testing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Bell className="w-5 h-5" />}
              Test invio
            </button>
          </div>
        </form>
      </div>

      <AnimatePresence>
        {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      </AnimatePresence>
    </div>
  );
};



// App Config Page - gestione centralizzata configurazione app Electron
const AppConfigPage = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/admin/app-config`);
      setConfig(res.data);
    } catch (err) {
      setToast({ message: "Errore caricamento configurazione", type: "error" });
    }
    setLoading(false);
  };

  useEffect(() => { fetchConfig(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...config };
      delete payload.updated_at;
      const res = await axios.put(`${API}/admin/app-config`, payload);
      setConfig(res.data);
      setToast({ message: "Configurazione salvata!", type: "success" });
    } catch (err) {
      setToast({ message: err.response?.data?.detail || "Errore salvataggio", type: "error" });
    }
    setSaving(false);
  };

  const update = (key, value) => setConfig({ ...config, [key]: value });

  if (loading || !config) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-netflix-red" /></div>;
  }

  return (
    <div className="animate-fade-in max-w-4xl">
      <div className="mb-6">
        <h2 className="font-headings text-4xl">Configurazione App</h2>
        <p className="text-zinc-400 mt-2">Impostazioni centralizzate che l'app Electron legge all'avvio. Le modifiche sono immediate — nessuna ricompilazione.</p>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        {/* Sezione Versioni */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6">
          <h3 className="font-headings text-xl mb-4 flex items-center gap-2"><Activity className="w-5 h-5 text-blue-400" /> Versione App</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Versione minima supportata</label>
              <input
                data-testid="config-min-version"
                type="text"
                value={config.min_version || ""}
                onChange={(e) => update("min_version", e.target.value)}
                placeholder="1.0.0"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
              />
              <p className="text-xs text-zinc-500 mt-1">Le versioni app inferiori verranno bloccate se "Forza aggiornamento" è attivo</p>
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Versione più recente disponibile</label>
              <input
                data-testid="config-latest-version"
                type="text"
                value={config.latest_version || ""}
                onChange={(e) => update("latest_version", e.target.value)}
                placeholder="1.0.0"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
              />
              <p className="text-xs text-zinc-500 mt-1">Usata per notificare aggiornamenti disponibili</p>
            </div>
          </div>
          <label className="flex items-center gap-3 mt-4 cursor-pointer">
            <input
              data-testid="config-force-update"
              type="checkbox"
              checked={config.force_update || false}
              onChange={(e) => update("force_update", e.target.checked)}
              className="w-5 h-5 accent-netflix-red"
            />
            <span className="text-sm">Forza aggiornamento — blocca app con versione inferiore alla minima</span>
          </label>
        </div>

        {/* Sezione Versione Android */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6">
          <h3 className="font-headings text-xl mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-green-400" /> Versione App Android (Fire TV / Mobile)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Versione minima Android</label>
              <input
                data-testid="config-android-min-version"
                type="text"
                value={config.android_min_version || ""}
                onChange={(e) => update("android_min_version", e.target.value)}
                placeholder="1.0.0"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
              />
              <p className="text-xs text-zinc-500 mt-1">App con versione inferiore vengono bloccate (forza update)</p>
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Versione Android più recente</label>
              <input
                data-testid="config-android-latest-version"
                type="text"
                value={config.android_latest_version || ""}
                onChange={(e) => update("android_latest_version", e.target.value)}
                placeholder="1.2.0"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
              />
              <p className="text-xs text-zinc-500 mt-1">Mostra suggerimento di aggiornamento agli utenti</p>
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm text-zinc-400 mb-2">URL APK (download diretto)</label>
            <input
              data-testid="config-android-apk-url"
              type="url"
              value={config.android_apk_url || ""}
              onChange={(e) => update("android_apk_url", e.target.value)}
              placeholder="https://app.hunter1.org/downloads/sktv-android-app.apk"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
            />
            <p className="text-xs text-zinc-500 mt-1">L'app aprirà questo URL per scaricare la nuova APK</p>
          </div>
          <div className="mt-4 flex items-center justify-between gap-4 bg-zinc-900/60 border border-zinc-800 rounded-sm p-4">
            <div>
              <label className="block text-sm font-bold mb-1">Avviso VPN all'avvio (utenti diretti admin)</label>
              <p className="text-xs text-zinc-500">
                Se attivo, i TUOI utenti (non assegnati a un reseller) vedranno il popup VPN all'apertura dell'app. Ogni reseller gestisce il proprio dal suo pannello.
              </p>
            </div>
            <button
              type="button"
              data-testid="config-vpn-popup-toggle"
              onClick={() => update("vpn_popup_enabled", !(config.vpn_popup_enabled !== false))}
              className={`relative w-14 h-7 rounded-full transition-colors flex-shrink-0 ${config.vpn_popup_enabled !== false ? "bg-netflix-red" : "bg-zinc-700"}`}
            >
              <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full transition-all ${config.vpn_popup_enabled !== false ? "left-7" : "left-0.5"}`} />
            </button>
          </div>
          <div className="mt-4">
            <label className="block text-sm text-zinc-400 mb-2">Qualità streaming (controllo banda sorgente)</label>            <select
              data-testid="config-stream-quality-policy"
              value={config.stream_quality_policy || "auto"}
              onChange={(e) => update("stream_quality_policy", e.target.value)}
              className="w-full md:w-96 bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
            >
              <option value="auto">Auto — massima qualità disponibile (FHD)</option>
              <option value="hd_start">Parti in HD — l'utente può passare a FHD manualmente</option>
              <option value="hd_cap">Massimo HD — FHD/4K nascosti per tutti</option>
            </select>
            <p className="text-xs text-zinc-500 mt-1">
              Applicata automaticamente a tutte le app Android al prossimo avvio. "Parti in HD" dimezza la banda consumata dalla sorgente mantenendo la scelta all'utente.
            </p>
          </div>
          <div className="mt-4">
            <label className="block text-sm text-zinc-400 mb-2">Note di rilascio (opzionale)</label>
            <textarea
              data-testid="config-android-changelog"
              value={config.android_changelog || ""}
              onChange={(e) => update("android_changelog", e.target.value)}
              placeholder="Cosa è cambiato in questa versione…"
              rows={3}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none resize-none"
            />
          </div>
        </div>

        {/* Sezione Promemoria scadenza globale (admin) */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6">
          <h3 className="font-headings text-xl mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-400" /> Promemoria Scadenza
          </h3>
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Soglia "in scadenza" per i tuoi utenti (giorni)</label>
            <input
              data-testid="config-expiry-threshold"
              type="number"
              min="1"
              max="365"
              value={config.expiry_threshold_days || 7}
              onChange={(e) => update("expiry_threshold_days", parseInt(e.target.value, 10) || 7)}
              className="w-full md:w-48 bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
            />
            <p className="text-xs text-zinc-500 mt-1">
              All'avvio l'app mostra il popup "Account in scadenza" se mancano ≤ N giorni.
              I reseller hanno una soglia indipendente impostata sul loro account.
            </p>
          </div>
        </div>

        {/* Sezione Manutenzione */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6">
          <h3 className="font-headings text-xl mb-4 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-yellow-400" /> Modalità Manutenzione</h3>
          <label className="flex items-center gap-3 mb-4 cursor-pointer">
            <input
              data-testid="config-maintenance-mode"
              type="checkbox"
              checked={config.maintenance_mode || false}
              onChange={(e) => update("maintenance_mode", e.target.checked)}
              className="w-5 h-5 accent-netflix-red"
            />
            <span className="text-sm font-medium">Attiva modalità manutenzione (blocca l'uso dell'app)</span>
          </label>
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Messaggio manutenzione</label>
            <textarea
              data-testid="config-maintenance-message"
              value={config.maintenance_message || ""}
              onChange={(e) => update("maintenance_message", e.target.value)}
              placeholder="Es: Server in manutenzione. Torneremo online alle 22:00."
              rows={3}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none resize-none"
            />
          </div>
        </div>

        {/* Sezione Broadcast */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6">
          <h3 className="font-headings text-xl mb-4 flex items-center gap-2"><MessageSquare className="w-5 h-5 text-cyan-400" /> Messaggio Broadcast</h3>
          <label className="flex items-center gap-3 mb-4 cursor-pointer">
            <input
              data-testid="config-broadcast-enabled"
              type="checkbox"
              checked={config.broadcast_enabled || false}
              onChange={(e) => update("broadcast_enabled", e.target.checked)}
              className="w-5 h-5 accent-netflix-red"
            />
            <span className="text-sm font-medium">Mostra messaggio broadcast all'avvio dell'app</span>
          </label>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Titolo</label>
              <input
                data-testid="config-broadcast-title"
                type="text"
                value={config.broadcast_title || ""}
                onChange={(e) => update("broadcast_title", e.target.value)}
                placeholder="Es: Nuovi canali disponibili!"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Messaggio</label>
              <textarea
                data-testid="config-broadcast-message"
                value={config.broadcast_message || ""}
                onChange={(e) => update("broadcast_message", e.target.value)}
                rows={3}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none resize-none"
              />
            </div>
          </div>
        </div>

        {/* Sezione URL/DNS */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6">
          <h3 className="font-headings text-xl mb-4 flex items-center gap-2"><Globe className="w-5 h-5 text-purple-400" /> URL e DNS</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">DNS provider fallback</label>
              <input
                data-testid="config-default-dns"
                type="text"
                value={config.default_dns_url || ""}
                onChange={(e) => update("default_dns_url", e.target.value)}
                placeholder="http://provider.example.com:8080"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none font-mono text-sm"
              />
              <p className="text-xs text-zinc-500 mt-1">DNS Xtream da usare se l'utente non ne ha uno assegnato</p>
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">URL aggiornamenti app</label>
              <input
                data-testid="config-update-url"
                type="text"
                value={config.update_url || ""}
                onChange={(e) => update("update_url", e.target.value)}
                placeholder="http://TUO_IP/updates/"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none font-mono text-sm"
              />
              <p className="text-xs text-zinc-500 mt-1">URL da cui l'app Electron scarica gli aggiornamenti</p>
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">URL supporto/contatti</label>
              <input
                data-testid="config-support-url"
                type="text"
                value={config.support_url || ""}
                onChange={(e) => update("support_url", e.target.value)}
                placeholder="https://t.me/tuocanale"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Nome app</label>
              <input
                data-testid="config-app-name"
                type="text"
                value={config.app_name || ""}
                onChange={(e) => update("app_name", e.target.value)}
                placeholder="SK TV"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
              />
            </div>
          </div>
        </div>

        {config.updated_at && (
          <p className="text-xs text-zinc-500">Ultimo aggiornamento: {new Date(config.updated_at).toLocaleString("it-IT")}</p>
        )}

        <button
          data-testid="save-app-config-btn"
          type="submit"
          disabled={saving}
          className="bg-netflix-red hover:bg-red-700 text-white font-bold rounded-sm px-6 py-3 flex items-center gap-2 transition-all active:scale-95 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
          {saving ? "Salvando..." : "Salva Configurazione"}
        </button>
      </form>

      <VpnProfilesSection setToast={setToast} />

      <AnimatePresence>
        {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      </AnimatePresence>
    </div>
  );
};

// Sezione Profili VPN: l'admin carica i file .ovpn che l'app scarica e usa
const VpnProfilesSection = ({ setToast }) => {
  const [profiles, setProfiles] = useState([]);
  const [form, setForm] = useState({ name: "", country: "", username: "", password: "", content: "" });
  const [fileName, setFileName] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchProfiles = async () => {
    try {
      const res = await axios.get(`${API}/admin/vpn-profiles`);
      setProfiles(res.data);
    } catch (err) { console.warn("caricamento profili VPN fallito", err); }
  };
  useEffect(() => { fetchProfiles(); }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = () => {
      setForm((prev) => ({
        ...prev,
        content: String(reader.result || ""),
        name: prev.name || f.name.replace(/\.ovpn$/i, ""),
      }));
    };
    reader.readAsText(f);
  };

  const handleUpload = async () => {
    if (!form.content || !form.name || !form.country) {
      setToast({ message: "Compila nome, paese e carica il file .ovpn", type: "error" });
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/vpn-profiles`, form);
      setForm({ name: "", country: "", username: "", password: "", content: "" });
      setFileName("");
      setToast({ message: "Profilo VPN caricato!", type: "success" });
      fetchProfiles();
    } catch (err) {
      setToast({ message: err.response?.data?.detail || "Errore caricamento profilo", type: "error" });
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo profilo VPN?")) return;
    try {
      await axios.delete(`${API}/admin/vpn-profiles/${id}`);
      setToast({ message: "Profilo eliminato", type: "success" });
      fetchProfiles();
    } catch (err) {
      setToast({ message: "Errore eliminazione", type: "error" });
    }
  };

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6 mt-6" data-testid="vpn-profiles-section">
      <h3 className="font-headings text-xl mb-2 flex items-center gap-2">
        <Shield className="w-5 h-5 text-emerald-400" /> Profili VPN (app Android)
      </h3>
      <p className="text-xs text-zinc-500 mb-4">
        Carica i file .ovpn con le credenziali: l'app li scarica automaticamente e mostra i paesi disponibili agli utenti.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <input
          data-testid="vpn-profile-name"
          type="text" placeholder="Nome profilo (es. Server Milano)"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
        />
        <input
          data-testid="vpn-profile-country"
          type="text" placeholder="Paese (es. Italia 🇮🇹)"
          value={form.country}
          onChange={(e) => setForm({ ...form, country: e.target.value })}
          className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
        />
        <input
          data-testid="vpn-profile-username"
          type="text" placeholder="Utente VPN (opzionale)"
          value={form.username}
          onChange={(e) => setForm({ ...form, username: e.target.value })}
          className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
        />
        <input
          data-testid="vpn-profile-password"
          type="password" placeholder="Password VPN (opzionale)"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-3 text-white focus:border-netflix-red outline-none"
        />
      </div>
      <div className="flex items-center gap-3 mb-4">
        <label className="bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-sm px-4 py-2 cursor-pointer text-sm" data-testid="vpn-profile-file-label">
          Scegli file .ovpn
          <input type="file" accept=".ovpn,.conf,.txt" className="hidden" onChange={onFile} data-testid="vpn-profile-file" />
        </label>
        <span className="text-xs text-zinc-400">{fileName || "Nessun file selezionato"}</span>
        <button
          data-testid="vpn-profile-upload-btn"
          type="button"
          onClick={handleUpload}
          disabled={saving}
          className="ml-auto bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-sm px-5 py-2 text-sm flex items-center gap-2 transition-all active:scale-95 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Carica profilo
        </button>
      </div>

      {profiles.length === 0 ? (
        <p className="text-sm text-zinc-500" data-testid="vpn-profiles-empty">Nessun profilo VPN caricato.</p>
      ) : (
        <div className="space-y-2" data-testid="vpn-profiles-list">
          {profiles.map((p) => (
            <div key={p.id} className="flex items-center justify-between bg-zinc-800/60 border border-zinc-700 rounded-sm px-4 py-3">
              <div>
                <span className="font-semibold">{p.country}</span>
                <span className="text-zinc-400 text-sm ml-2">{p.name}</span>
                {p.username && <span className="text-xs text-zinc-500 ml-2">utente: {p.username}</span>}
              </div>
              <button
                data-testid={`vpn-profile-delete-${p.id}`}
                onClick={() => handleDelete(p.id)}
                className="text-red-400 hover:text-red-300 text-sm"
              >
                Elimina
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// Connessioni attive (monitoraggio + protezione anti-sharing)
const ConnectionsPage = () => {
  const [sessions, setSessions] = useState([]);
  const [protection, setProtection] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API}/admin/active-sessions`);
      setSessions(res.data.sessions || []);
      setProtection(res.data.protection_enabled);
      setLoadError(false);
    } catch (e) { setLoadError(true); }
    setLoading(false);
  };

  useEffect(() => {
    fetchSessions();
    const iv = setInterval(fetchSessions, 10000);
    return () => clearInterval(iv);
  }, []);

  const toggleProtection = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/app-config`, { connection_protection: !protection });
      setProtection(!protection);
    } catch (e) { alert("Errore salvataggio"); }
    setSaving(false);
  };

  const fmtDuration = (s) => {
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h ${m % 60}m`;
  };

  const watching = sessions.filter((s) => s.watching);
  const multiIpUsers = new Set(sessions.filter((s) => s.multi_ip).map((s) => s.username));

  return (
    <div data-testid="connections-page">
      {loadError && (
        <div data-testid="connections-load-error" className="mb-4 bg-red-950/60 border border-red-800 text-red-300 rounded-sm px-4 py-3 text-sm flex flex-wrap gap-3 items-center justify-between">
          <span>Impossibile caricare le connessioni attive: i dati mostrati potrebbero non essere aggiornati.</span>
          <button onClick={fetchSessions} className="underline font-bold hover:text-white">Riprova</button>
        </div>
      )}
      <div className="flex flex-wrap gap-3 items-center justify-between mb-8">
        <div>
          <h2 className="font-headings text-5xl mb-2">CONNESSIONI ATTIVE</h2>
          <p className="text-zinc-500">Chi sta guardando cosa, in tempo reale (aggiornamento ogni 10s)</p>
        </div>
        <button onClick={fetchSessions} data-testid="connections-refresh-btn"
          className="px-4 py-2 bg-zinc-900 hover:bg-zinc-800 rounded-sm text-zinc-300 border border-zinc-800">
          Aggiorna
        </button>
      </div>

      {/* Toggle protezione */}
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-5 mb-6 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Shield className={`w-5 h-5 ${protection ? "text-green-500" : "text-zinc-500"}`} />
            <h3 className="font-semibold text-lg">Protezione anti-condivisione</h3>
          </div>
          <p className="text-sm text-zinc-500 max-w-2xl">
            Se attiva, quando un account è già in visione da un IP, ogni nuovo stream da un IP
            diverso viene <span className="text-red-400">rifiutato con avviso</span> sul dispositivo.
            Dispositivi sulla stessa rete (stesso IP) non vengono bloccati.
          </p>
        </div>
        <button
          data-testid="connection-protection-toggle"
          onClick={toggleProtection}
          disabled={saving}
          className={`relative w-14 h-7 rounded-full transition-colors flex-shrink-0 ml-6
            ${protection ? "bg-green-600" : "bg-zinc-700"}`}
        >
          <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full transition-all
            ${protection ? "left-7" : "left-0.5"}`} />
        </button>
      </div>

      {/* Statistiche rapide */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-4">
          <p className="text-zinc-500 text-sm">In visione ora</p>
          <p className="text-3xl font-bold text-green-500" data-testid="watching-now-count">{watching.length}</p>
        </div>
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-4">
          <p className="text-zinc-500 text-sm">Sessioni recenti (3 min)</p>
          <p className="text-3xl font-bold">{sessions.length}</p>
        </div>
        <div className={`border rounded-sm p-4 ${multiIpUsers.size > 0 ? "bg-red-950/40 border-red-900" : "bg-zinc-900/60 border-zinc-800"}`}>
          <p className="text-zinc-500 text-sm">Account multi-IP (possibile sharing)</p>
          <p className={`text-3xl font-bold ${multiIpUsers.size > 0 ? "text-red-500" : ""}`} data-testid="multi-ip-count">{multiIpUsers.size}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-netflix-red" /></div>
      ) : sessions.length === 0 ? (
        <div className="text-center py-16 text-zinc-500 border border-dashed border-zinc-800 rounded-sm">
          <Activity className="w-10 h-10 mx-auto mb-3 opacity-40" />
          Nessuna connessione attiva al momento.<br />
          <span className="text-xs">Le sessioni compaiono quando le app (v1.23.0+) riproducono uno stream.</span>
        </div>
      ) : (
        <div className="border border-zinc-800 rounded-sm overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-zinc-900 text-zinc-400 text-left">
              <tr>
                <th className="px-4 py-3">Stato</th>
                <th className="px-4 py-3">Utente</th>
                <th className="px-4 py-3">Contenuto</th>
                <th className="px-4 py-3">IP</th>
                <th className="px-4 py-3">Dispositivo</th>
                <th className="px-4 py-3">Durata</th>
                <th className="px-4 py-3">Ultimo ping</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s, i) => (
                <tr key={s.key || i} data-testid={`session-row-${i}`}
                  className={`border-t border-zinc-800/70 ${s.multi_ip ? "bg-red-950/30" : "hover:bg-zinc-900/50"}`}>
                  <td className="px-4 py-3">
                    {s.watching
                      ? <span className="inline-flex items-center gap-1.5 text-green-500"><span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />LIVE</span>
                      : <span className="text-zinc-600">inattiva</span>}
                  </td>
                  <td className="px-4 py-3 font-medium">
                    {s.username}
                    {s.multi_ip && <span className="ml-2 text-xs px-1.5 py-0.5 bg-red-900/60 text-red-400 rounded">MULTI-IP</span>}
                  </td>
                  <td className="px-4 py-3 text-zinc-300">
                    {s.stream_name || <span className="text-zinc-600">—</span>}
                    {s.stream_type && <span className="ml-2 text-xs text-zinc-500 uppercase">{s.stream_type}</span>}
                  </td>
                  <td className="px-4 py-3 font-mono text-zinc-400">{s.ip}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">{s.device_id ? s.device_id.slice(0, 14) + "…" : "—"}</td>
                  <td className="px-4 py-3 text-zinc-400">{fmtDuration(s.duration_sec)}</td>
                  <td className="px-4 py-3 text-zinc-500">{s.seconds_ago}s fa</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};


// Admin Layout
const AdminLayout = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const renderContent = () => {
    switch (activeTab) {
      case "dashboard": return <DashboardPage />;
      case "dns": return <DNSPage />;
      case "resellers": return <ResellersPage />;
      case "users": return <UsersPage />;
      case "stats": return <StatsPage />;
      case "bandwidth": return <BandwidthPage />;
      case "connections": return <ConnectionsPage />;
      case "diagnostics": return <DiagnosticsPage />;
      case "messages": return <MessagesPage />;
      case "app-config": return <AppConfigPage />;
      case "settings": return <SettingsPage />;
      default: return <DashboardPage />;
    }
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Header mobile con hamburger */}
      <header className="md:hidden fixed top-0 inset-x-0 z-40 bg-zinc-950 border-b border-zinc-800 flex items-center gap-3 px-4 py-3">
        <button
          data-testid="mobile-menu-btn"
          onClick={() => setMobileMenuOpen(true)}
          className="text-zinc-300 hover:text-white p-1"
        >
          <Menu className="w-6 h-6" />
        </button>
        <img src="/logo.png" alt="SK TV" className="h-7 w-auto" />
        <span className="font-headings text-2xl text-netflix-red tracking-wider">SK TV</span>
      </header>
      {/* Backdrop drawer mobile */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/70 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      <Sidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        mobileOpen={mobileMenuOpen}
        onCloseMobile={() => setMobileMenuOpen(false)}
      />
      <main className="md:ml-64 p-4 pt-[70px] md:p-8 md:pt-8">
        {renderContent()}
      </main>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

// App
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <RoleRouter />
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

const RoleRouter = () => {
  const { role, logout } = useAuth();
  return role === "reseller"
    ? <ResellerLayout logout={logout} />
    : <AdminLayout />;
};

export default App;
